"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useLocale } from "next-intl";
import { stegaClean } from "next-sanity";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/dialog";
import { MarketingPopupCard } from "@/components/marketing-popup";
import type { PopupDismissMethod } from "@/lib/analytics/push-event";
import { pushEvent } from "@/lib/analytics/push-event";
import { usePathname } from "@/i18n/navigation";
import type { MarketingPopupFragmentResult } from "@/lib/sanity/sanity.types";
import { getI18nString } from "@/lib/utils";
import type { ResolvedPopup } from "./resolve-marketing-popup";
import { resolveMarketingPopup } from "./resolve-marketing-popup";
const SESSION_KEY_PREFIX = "vaw-popup-seen:";

// Let the page settle before the pop-up greets the visitor.
const OPEN_DELAY_MS = 800;
interface MarketingPopupModalProps {
  global: MarketingPopupFragmentResult["global"];
  overrides: MarketingPopupFragmentResult["overrides"];
  /**
   * Disables the once-per-session gate so editors reviewing in draft mode /
   * Presentation preview see the pop-up on every load. Does not bypass the
   * schedule — an out-of-window campaign stays hidden in preview too.
   */
  skipSessionGate?: boolean;
}
function hasSeenThisSession(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}
function markSeenThisSession(key: string): void {
  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    // Storage unavailable (e.g. blocked) — the pop-up simply shows again.
  }
}
export function MarketingPopupModal(t0) {
  const $ = _c(34);
  const {
    global,
    overrides,
    skipSessionGate: t1
  } = t0;
  const skipSessionGate = t1 === undefined ? false : t1;
  const pathname = usePathname();
  const locale = useLocale();
  const [active, setActive] = useState(null);
  let t2;
  let t3;
  if ($[0] !== global || $[1] !== locale || $[2] !== overrides || $[3] !== pathname || $[4] !== skipSessionGate) {
    t2 = () => {
      const resolved = resolveMarketingPopup({
        global,
        overrides,
        pathname,
        locale,
        now: new Date()
      });
      if (!resolved) {
        return;
      }
      const key = `${SESSION_KEY_PREFIX}${resolved._id}`;
      if (!skipSessionGate && hasSeenThisSession(key)) {
        return;
      }
      const timer = window.setTimeout(() => {
        markSeenThisSession(key);
        setActive(resolved);
        pushEvent({
          event: "popup_view",
          popup_id: resolved._id,
          popup_name: getI18nString(resolved.headline) || undefined,
          popup_variant: stegaClean(resolved.variant) ?? undefined,
          page_path: pathname
        });
      }, OPEN_DELAY_MS);
      return () => {
        window.clearTimeout(timer);
        setActive(null);
      };
    };
    t3 = [global, overrides, pathname, locale, skipSessionGate];
    $[0] = global;
    $[1] = locale;
    $[2] = overrides;
    $[3] = pathname;
    $[4] = skipSessionGate;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  if (!active) {
    return null;
  }
  let t4;
  if ($[7] !== active._id || $[8] !== active.headline || $[9] !== pathname) {
    t4 = popup_action => {
      pushEvent({
        event: "popup_click",
        popup_id: active._id,
        popup_name: getI18nString(active.headline) || undefined,
        popup_action,
        page_path: pathname
      });
    };
    $[7] = active._id;
    $[8] = active.headline;
    $[9] = pathname;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const recordClick = t4;
  let t5;
  if ($[11] !== recordClick) {
    t5 = method => {
      recordClick(method);
      setActive(null);
    };
    $[11] = recordClick;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const dismiss = t5;
  const variant = stegaClean(active.variant) ?? "editorial-split";
  let t6;
  if ($[13] !== dismiss) {
    t6 = () => dismiss("backdrop_or_escape");
    $[13] = dismiss;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = variant === "glass" ? "max-w-xl" : undefined;
  let t8;
  if ($[15] !== active.headline) {
    t8 = getI18nString(active.headline, "Campaign pop-up");
    $[15] = active.headline;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <DialogTitle className="sr-only">{t8}</DialogTitle>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== dismiss) {
    t10 = () => dismiss("close");
    $[19] = dismiss;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== recordClick) {
    t11 = () => recordClick("cta");
    $[21] = recordClick;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== active || $[24] !== t10 || $[25] !== t11) {
    t12 = <MarketingPopupCard popup={active} onClose={t10} onCtaClick={t11} />;
    $[23] = active;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t7 || $[29] !== t9) {
    t13 = <DialogContent position="center" transition="pop" showCloseButton={false} aria-describedby={undefined} className={t7}>{t9}{t12}</DialogContent>;
    $[27] = t12;
    $[28] = t7;
    $[29] = t9;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13 || $[32] !== t6) {
    t14 = <Dialog open={true} onOpenChange={t6}>{t13}</Dialog>;
    $[31] = t13;
    $[32] = t6;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}